










import { COLOR_PALETTE, COLOR_PALETTE_DARKER } from "@/colors";
import { tickTimeFormat } from "@/utils/d3/time";
import * as c from "@/utils/chart";
import { Axis } from "@/types/chart";
import { Series } from "@/types/common";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import * as d3 from "d3";

@Component
export default class BaseChart extends Vue {
  mounted() {
    // $el is from the Vue API.
    // See: https://vuejs.org/v2/api/#el
    this.d3Node = d3.select(this.$el);

    /**
     * Responsive charts
     *
     * Listen for window resize
     * Calculate new chart width
     * Re-render chart
     */

    this.$nextTick(function() {
      window.addEventListener("resize", this.setChartWidth);
      this.setChartWidth();
    });
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.setChartWidth);
  }

  @Prop({ required: true })
  series!: Series;

  @Prop({ required: true })
  chartID!: string;

  // Default to average, sum chart, or first series for chart
  // only used for processed stats, not debug mode
  @Prop({ required: false, default: 0 })
  chartIndex!: number;

  @Prop({ required: false, default: true })
  isCard!: boolean;

  // Render when data has been returned by the API
  @Watch("series")
  onSeriesChange() {
    this.renderChart();
  }

  /**
   * Initial render
   *
   * Performs initial render because `resize` fires once on page load,
   * which sets the chart width
   *
   * Re-render chart when window resizes
   */
  @Watch("chartWidth")
  onChartWidthChange() {
    this.renderChart();
  }

  // `div.chart-container`, set in in mounted lifecycle hook
  d3Node: any;

  chartWidth = 0;
  chartHeight = 0;

  get svgContainer() {
    return this.d3Node.select(".chart");
  }

  get cardClass() {
    return this.isCard ? "card" : "";
  }

  setChartWidth() {
    this.chartWidth = parseInt(this.svgContainer.style("width"));

    // 1100 should match `$break-large` in `@/scss/_breakpoints.scss`
    // Values should match CSS `$chart-height-*` styles in `@/components/ProcessedStatsChartDisabled.vue`
    if (document.documentElement.clientWidth < 1100) {
      this.chartHeight = 300;
    } else {
      this.chartHeight = 400;
    }
  }

  renderChart() {
    // Removes the selected elements from the document.
    // https://github.com/d3/d3-selection/blob/v1.4.0/README.md#selection_remove
    this.d3Node.selectAll("svg").remove();
    this.d3Node.selectAll(".tooltip").remove();
    this.d3Node.selectAll(".legend").remove();

    if (!this.series || (this.series && this.series.values && this.series.values.length === 0)) {
      return;
    }

    const values = this.series.values;
    const chartID = this.chartID;

    // Remove 'time' as first item
    const metrics = this.series.columns.slice(1);
    const columns = this.series.columns;

    // Values should match CSS `$chart-margin-*` styles in `@/components/ProcessedStatsChartDisabled.vue`
    const margin = { top: 20, right: 70, bottom: 50, left: 70 };

    const widthContainer = this.chartWidth;
    const heightContainer = this.chartHeight;

    const widthChart = widthContainer - margin.left - margin.right;
    const heightChart = heightContainer - margin.top - margin.bottom;

    // Add responsive chart canvas
    const svg = this.svgContainer
      .append("svg")
      .attr("width", widthContainer)
      .attr("height", heightContainer)
      .attr("preserveAspectRatio", "none");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Add D3 time scale for x-axis
    // https://github.com/d3/d3-scale#scaleTime
    const x = d3
      .scaleTime()
      .domain([new Date(values[0][0]), new Date(values[values.length - 1][0])])
      .range([0, widthChart]);

    // Add x-axis ticks
    g.append("g")
      .attr("class", "x axis")
      .attr("transform", `translate(0,${heightChart})`)
      .call(
        d3
          .axisBottom(x)
          .tickFormat(tickTimeFormat)
          .tickSizeOuter(0)
          .tickSizeInner(0),
      )
      .call((g: any) => g.select(".domain").remove())
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-65)");

    // Add scale for left y-axis metrics
    const maxLeft = c.findMaxForYAxis(chartID, Axis.LEFT, metrics, values);
    const yLeft = d3
      .scaleLinear()
      .domain([0, maxLeft])
      .range([heightChart, 0]);

    // Add scale for right y-axis metrics
    let yRight: any;
    if (c.hasAxisRight(chartID)) {
      const maxRight = c.findMaxForYAxis(chartID, Axis.RIGHT, metrics, values);
      yRight = d3
        .scaleLinear()
        .domain([0, maxRight])
        .range([heightChart, 0]);
    }

    // Add left y-axis ticks
    const axisLeft = d3
      .axisLeft(yLeft)
      .tickFormat(d => {
        return c.getTickFormat(chartID, Axis.LEFT, d);
      })
      .tickSizeOuter(0)
      .tickSizeInner(0);
    g.append("g")
      .attr("class", `y-left axis`)
      .attr("transform", `translate(-2,0)`)
      .call(axisLeft)
      .call((g: any) => g.select(".domain").remove());

    // Add label for left y-axis
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - heightChart / 2)
      .attr("dy", "1em")
      .attr("class", "chart-label")
      .style("text-anchor", "middle")
      .text(c.getYLabel(chartID, Axis.LEFT));

    // Add x-axis gridlines
    g.append("g")
      .attr("class", "x chart-gridline")
      .attr("transform", "translate(0," + heightChart + ")")
      .call(d3.axisBottom(x).tickSize(-heightChart))
      .call((g: any) => g.selectAll("text").remove());

    // Add y-axis gridlines
    g.append("g")
      .attr("class", "y chart-gridline")
      .call(d3.axisLeft(yLeft).tickSize(-widthChart))
      .call((g: any) => g.selectAll("text").remove());

    // Create stacked chart
    let valuesStacked: any;
    if (c.isStacked(chartID)) {
      let valuesForStacking = values;

      /**
       * Stacked charts only have a left y-axis -- think about it ;-P
       *
       * Note: if values are in fractions that always sum to 1,
       * this check is unnecessary
       */
      if (c.formatIsPercentage(chartID, Axis.LEFT)) {
        valuesForStacking = c.stackValuesPercent(values);
      }

      const stack = d3.stack();

      stack.keys(metrics);

      // Format values for d3.stack
      const valuesStackable = valuesForStacking.map(point => {
        const valRow: any = {};
        columns.forEach((column, i) => {
          // safely assuming the first val is always time
          let val = point[i];
          if (i === 0) {
            // D3 complains if time is not a number
            val = d3.isoParse(point[i]);
          }
          valRow[column] = val;
        });
        return valRow;
      });

      valuesStacked = stack(valuesStackable);

      const area = d3
        .area()
        .x(function(d: any) {
          return x(d.data.time);
        })
        .y0(function(d) {
          return yLeft(d[0]);
        })
        .y1(function(d) {
          return yLeft(d[1]);
        });

      const layer = g
        .selectAll(".layer")
        .data(valuesStacked)
        .enter()
        .append("g")
        .attr("class", "layer");

      layer
        .append("path")
        .attr("class", "area")
        .style("fill", function(d: any) {
          return COLOR_PALETTE[d.index];
        })
        .attr("d", area);
    }

    // Define divs for the legend
    const legend = this.d3Node.append("div").attr("class", "legend");
    const legendLeft = legend.append("div").attr("class", "legend-left");
    const legendRight = legend.append("div").attr("class", "legend-right");

    c.setYAxis(chartID, Axis.LEFT, metrics, values, g, x, yLeft, legendLeft);

    if (c.hasAxisRight(chartID)) {
      legend.attr("class", "legend legend-two-y-axes");

      // Add right y-axis ticks
      const axisRight = d3
        .axisRight(yRight)
        .tickFormat(d => {
          return c.getTickFormat(chartID, Axis.RIGHT, d);
        })
        .tickSizeOuter(0)
        .tickSizeInner(0);
      g.append("g")
        .attr("class", `y-right axis`)
        .attr("transform", `translate(${widthChart + 4},0)`)
        .call(axisRight)
        .call((g: any) => g.select(".domain").remove());

      // Add label for right y-axis
      g.append("text")
        .attr("transform", `translate(${widthChart + margin.right},${heightChart / 2}),rotate(90)`)
        .attr("dy", "1em")
        .attr("class", "chart-label")
        .style("text-anchor", "middle")
        .text(c.getYLabel(chartID, Axis.RIGHT));

      c.setYAxis(chartID, Axis.RIGHT, metrics, values, g, x, yRight, legendRight);
    }

    // Define div for the tooltip
    const randomInt = Math.floor(Math.random() * Math.pow(2, 64));
    const tooltipID = `tooltip-${randomInt}`;
    const tooltip = this.d3Node
      .append("div")
      .attr("class", "tooltip")
      .attr("id", tooltipID)
      .style("display", "none");

    const valuesTooltip = metrics.map(function(metric, i) {
      return {
        metric: metric,
        values: values.map(value => {
          return {
            time: value[0],
            value: +value[i + 1],
          };
        }),
      };
    });

    const valuesCircles = c.isStacked(chartID) ? valuesStacked : valuesTooltip;

    // Add tooltip circles, positioned on hover
    g.selectAll(".circles")
      .data(valuesCircles)
      .enter()
      .append("g")
      .attr("class", "circles")
      .append("circle")
      .attr("r", 3)
      .attr("fill-opacity", 0);

    const focus = g
      .append("g")
      .attr("class", "focus")
      .style("display", "none");

    // Add vertical hover line
    focus
      .append("line")
      .attr("class", "x-hover-line hover-line")
      .attr("y1", 0)
      .attr("y2", heightChart);

    const timeScales = values.map(function(value) {
      return x(new Date(value[0]).getTime());
    });

    function mousemove(this: any) {
      if (focus.style("display") === "none") {
        focus.style("display", null);
        tooltip.style("display", null);
        g.selectAll(".circles circle").style("display", null);
      }

      const mouse = d3.mouse(this);
      const iRight = d3.bisect(timeScales, mouse[0]);
      const iLeft = iRight > 0 ? iRight - 1 : iRight;
      const diLeft = values[iLeft];
      const diRight = values[iRight];

      // get the closest x-axis position
      let iClosest: number;
      let time: Date;

      const dateMouseStr: any = x.invert(mouse[0]);
      const dateMouse = Date.parse(dateMouseStr);

      if (diRight !== undefined && dateMouse - Date.parse(diLeft[0]) > Date.parse(diRight[0]) - dateMouse) {
        time = new Date(Date.parse(diRight[0]));
        iClosest = iRight;
      } else {
        time = new Date(Date.parse(diLeft[0]));
        iClosest = iLeft;
      }

      // move vertical line along the x-axis with cursor
      focus.attr("transform", "translate(" + x(time) + ",0)");

      // Tooltop moves with cursor
      // display date and metric labels + values
      tooltip
        .html(() => {
          return c.tooltipTemplateTime(time);
        })
        .style("left", c.tooltipX(tooltip, tooltipID))
        .style("top", `${d3.event.layerY + 8}px`)
        .selectAll()
        .data(valuesTooltip)
        .enter()
        .append("div")
        .attr("class", "tooltip-metric")
        .html((d: any, metricIdx: number) => {
          return c.tooltipTemplateMetric(chartID, metricIdx, d, iClosest);
        });

      // Add circles to chart points on hover
      g.selectAll(".circles circle")
        // position on x-axis
        .attr("cx", function() {
          return x(time);
        })
        // position on y-axis
        .attr("cy", function(d: any) {
          if (c.isStacked(chartID)) {
            // If real value is close to zero, set position to zero,
            // because circles will be too close together.
            // Otherwise set position to stacked value
            const yPos = d[iClosest].data[d.key] < 0.01 ? 0 : d[iClosest][1];

            // Stacked charts only have a left y-axis
            return yLeft(yPos);
          } else {
            const yCircle = c.yAxisIs(chartID, Axis.LEFT, d.metric) ? yLeft : yRight;
            return yCircle(d.values[iClosest].value);
          }
        })
        // color circle stroke
        .style("stroke", function(d: any, metricIdx: number) {
          return COLOR_PALETTE_DARKER[metricIdx];
        })
        .style("stroke-width", 2);
    }

    // Add rectangle to detect mouse events
    svg
      .append("rect")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
      .attr("class", "overlay")
      .attr("width", widthChart)
      .attr("height", heightChart)
      .on("mouseout", () => {
        return c.mouseout(focus, tooltip, g);
      })
      .on("mousemove", mousemove);
  } // render chart
}
